import type { FaceSummary } from '@/store/api'

interface FaceOverlayProps {
  faces: FaceSummary[]
  visible: boolean
  selectedFaceId: string | null
  onFaceClick: (face: FaceSummary) => void
  imageRect?: { left: number; top: number; width: number; height: number } | null
}

function FaceOverlay({ faces, visible, selectedFaceId, onFaceClick, imageRect }: FaceOverlayProps) {
  if (!visible || faces.length === 0) return null

  // When imageRect is provided, position the overlay to cover only the visible image area
  // (compensates for object-contain letterboxing/pillarboxing)
  const containerStyle = imageRect
    ? { position: 'absolute' as const, left: imageRect.left, top: imageRect.top, width: imageRect.width, height: imageRect.height }
    : undefined
  const containerClass = imageRect
    ? 'pointer-events-none'
    : 'absolute inset-0 pointer-events-none'

  return (
    <div className={containerClass} style={containerStyle}>
      {faces.map((face) => {
        const isSelected = selectedFaceId === face.face_id
        return (
          <div
            key={face.face_id}
            className={`absolute pointer-events-auto cursor-pointer border-2 rounded-sm transition-colors ${
              isSelected
                ? 'border-cyan-300 bg-cyan-400/15'
                : 'border-cyan-400/70 hover:border-cyan-300 hover:bg-cyan-400/10'
            }`}
            style={{
              left: `${face.bbox.x * 100}%`,
              top: `${face.bbox.y * 100}%`,
              width: `${face.bbox.width * 100}%`,
              height: `${face.bbox.height * 100}%`,
            }}
            onClick={(e) => {
              e.stopPropagation()
              onFaceClick(face)
            }}
            title={[
              face.age != null ? `Age: ~${face.age}` : null,
              face.gender ?? null,
              `Confidence: ${Math.round(face.detection_confidence * 100)}%`,
            ]
              .filter(Boolean)
              .join(' | ')}
          />
        )
      })}
    </div>
  )
}

export default FaceOverlay
